/**
 * Marketing pages — public, unauthenticated.
 *
 * Copy authority: /tmp/vlk_pack/WEBSITE_COPY_COMPLETE.md +
 * PUBLIC_COPY.md + TRUST_SECURITY_PUBLIC_COPY.md (VeraLok Public
 * Creative Pack, 2026-08-30). Do not reintroduce over-detailed security
 * mechanics or unverified customer / certification / traction claims.
 *
 * Public surfaces implemented in this file:
 *   Home / HowItWorks / ForInstitutions / Hospitality / EnterpriseServices
 *   / Health / Finance / Government / Developers / TrustSecurity /
 *   Company / Leadership / Engage / Resources / Support / Contact /
 *   Status / SecurityReport / RequestDemo / Legal.*
 *
 * Legacy names preserved so App.js keeps importing:
 *   Product, Security, About, ForCitizens, FAQ.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

// VeraLok Public Presence (Creative Pack, 2026-08-30) — five approved
// premium 3D institutional visuals live at
// /marketing/institutions/*.png. Each composition preserves the
// VeraLok padlock + removable-key motif alongside a sector-specific
// 3D architectural scene (dark navy #010F23 with restrained
// #00B4E6 cyan illumination, layered depth, glow rings, glass
// materials, reflections). These are the primary institutional
// visuals; do not substitute icon-only replacements for them.
const INSTITUTION_IMAGES = {
  Hospitality:            "/marketing/institutions/hospitality.jpg",
  "Enterprise Services":  "/marketing/institutions/enterprise-services.jpg",
  Health:                 "/marketing/institutions/health.jpg",
  Finance:                "/marketing/institutions/finance.jpg",
  Government:             "/marketing/institutions/government.jpg",
};

// Approved 3D padlock + removable key master render — VeraLok's
// principal identity-control motif. Reused across pages that would
// otherwise be visual-lean, giving the site a consistent premium
// rhythm without inventing new artwork.
const PADLOCK_KEY_MASTER = "/marketing/padlock-key.png";

function PadlockKeyHero({ testid = "padlock-key-hero", size = "normal" }) {
  const cls = size === "compact" ? "max-w-lg aspect-square"
            : size === "wide"    ? "max-w-none aspect-[16/10]"
            :                       "max-w-2xl aspect-square";
  return (
    <div className={`vlk-panel vlk-3d-tilt vlk-3d-glow overflow-hidden mx-auto ${cls}`}
         style={{
           background:
             "radial-gradient(60% 55% at 50% 42%, rgba(0,180,230,0.16) 0%, rgba(7,27,53,0.85) 55%, var(--vlk-panel) 100%)",
         }}
         data-testid={testid}>
      <img src={PADLOCK_KEY_MASTER}
           alt="VeraLok padlock and removable key — the identity-control motif"
           className="w-full h-full object-contain block vlk-3d-float"
           loading="lazy" />
    </div>
  );
}

// ─── Shared building blocks ────────────────────────────────────────────────
function Eyebrow({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--vlk-cyan)] mb-4">
      {children}
    </div>
  );
}
function SectionTitle({ children, level = "h2", className = "" }) {
  const Tag = level;
  const size = level === "h1"
    ? "text-4xl sm:text-5xl lg:text-6xl"
    : "text-3xl sm:text-4xl";
  return (
    <Tag className={`${size} font-semibold tracking-tight vlk-reveal ${className}`}
         style={{ letterSpacing: "-0.01em" }}>
      {children}
    </Tag>
  );
}
function Lede({ children }) {
  return (
    <p className="text-white/70 text-lg max-w-2xl leading-relaxed vlk-reveal d1 mt-5">
      {children}
    </p>
  );
}
function PillCard({ title, body, testid }) {
  return (
    <div className="vlk-panel vlk-interactive p-6" data-testid={testid}>
      <div className="h-1 w-10 rounded-full mb-4"
           style={{ background: "var(--vlk-cyan)" }} />
      <div className="text-xl font-semibold mb-2"
           style={{ fontFamily: "var(--vlk-font-display)" }}>{title}</div>
      <p className="text-white/70 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
function CTA({ to, label, primary = true, testid }) {
  const cls = primary ? "vlk-btn-primary" : "vlk-btn-ghost";
  return <Link to={to} className={cls} data-testid={testid}>{label}</Link>;
}

// Head-meta helper (native DOM; avoids adding react-helmet-async dep just
// for public marketing pages).
function useDocumentMeta({ title, description, canonicalPath, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    const set = (selector, create, apply) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = create(); document.head.appendChild(el); }
      apply(el);
      return el;
    };
    if (description) {
      set('meta[name="description"]',
          () => Object.assign(document.createElement("meta"), { name: "description" }),
          (el) => el.setAttribute("content", description));
      set('meta[property="og:description"]',
          () => { const m = document.createElement("meta"); m.setAttribute("property", "og:description"); return m; },
          (el) => el.setAttribute("content", description));
    }
    if (title) {
      set('meta[property="og:title"]',
          () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; },
          (el) => el.setAttribute("content", title));
    }
    if (canonicalPath) {
      const canonical = `https://www.veralok.com${canonicalPath}`;
      set('link[rel="canonical"]',
          () => Object.assign(document.createElement("link"), { rel: "canonical" }),
          (el) => el.setAttribute("href", canonical));
      set('meta[property="og:url"]',
          () => { const m = document.createElement("meta"); m.setAttribute("property", "og:url"); return m; },
          (el) => el.setAttribute("content", canonical));
    }
    let ld;
    if (jsonLd) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.text = JSON.stringify(jsonLd);
      ld.dataset.vlkLd = "1";
      document.head.appendChild(ld);
    }
    return () => { if (ld && ld.parentNode) ld.parentNode.removeChild(ld); };
  }, [title, description, canonicalPath, jsonLd]);
}

// ─── Home ──────────────────────────────────────────────────────────────────
export function Home() {
  useDocumentMeta({
    title: "VeraLok — Identity you control",
    description: "Verify your identity, share deliberately and manage access with consent, transparency and control.",
    canonicalPath: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "VeraLok Holdings Inc.",
          url: "https://www.veralok.com",
          logo: "https://www.veralok.com/icon-512.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "251 Little Falls Drive",
            addressLocality: "Wilmington",
            addressRegion: "DE",
            postalCode: "19808",
            addressCountry: "US",
          },
          sameAs: [
            "https://www.linkedin.com/company/veralok/",
            "https://www.facebook.com/profile.php?id=61593996629993",
            "https://www.youtube.com/channel/UCT_kw29HU2AZQfRryHINMrA",
          ],
        },
        {
          "@type": "WebSite",
          name: "VeraLok",
          url: "https://www.veralok.com",
        },
      ],
    },
  });
  return (
    <div className="space-y-24 relative" data-testid="marketing-home">
      <section className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center pt-4 relative">
        <div>
          <Eyebrow>VeraLok</Eyebrow>
          <SectionTitle level="h1">
            Identity you control.
          </SectionTitle>
          <Lede>
            Verify your identity. Share deliberately. Manage access with clarity and control.
          </Lede>
          <div className="mt-10 flex flex-wrap gap-3 vlk-reveal d3">
            <CTA to="/citizen" label="Get VeraLok" testid="home-cta-get-veralok" />
            <CTA to="/institutions" label="For Institutions" primary={false} testid="home-cta-institutions" />
          </div>
        </div>
        <div className="vlk-reveal d2">
          <img
            src="/marketing/hero-padlock-key.png"
            alt="VeraLok padlock and removable key"
            className="w-full h-auto rounded-3xl border border-white/8 shadow-2xl vlk-3d-float"
            data-testid="home-hero-visual"
          />
        </div>
      </section>

      <section className="relative">
        <Eyebrow>Verification is only the beginning</Eyebrow>
        <SectionTitle>VeraLok gives people a practical control layer for verified identity.</SectionTitle>
        <Lede>
          Review requests, approve what is shared, retain visibility and manage access.
        </Lede>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <PillCard title="Verify"
                    body="Create a verified VeraLok identity through supported enrollment."
                    testid="home-step-verify" />
          <PillCard title="Consent"
                    body="Review an institution's request before identity information is shared."
                    testid="home-step-consent" />
          <PillCard title="Control"
                    body="Manage identity access and retain visibility into activity."
                    testid="home-step-control" />
        </div>
      </section>

      <section className="border-t border-white/8 pt-16 relative">
        <Eyebrow>End-to-end sequence</Eyebrow>
        <SectionTitle>The full VeraLok interaction, at a glance.</SectionTitle>
        <div className="mt-8 vlk-panel overflow-hidden">
          <img src="/marketing/motion-storyboard.png"
               alt="VeraLok six-panel product motion storyboard — identity you control, institutional interaction, review and consent, permitted result, control, company"
               className="w-full h-auto block vlk-3d-float"
               loading="lazy" />
        </div>
        <div className="mt-8">
          <CTA to="/how-it-works" label="How VeraLok works" primary={false} testid="home-cta-how" />
        </div>
      </section>

      <section className="border-t border-white/8 pt-16 relative">
        <Eyebrow>One architecture. Five institutional environments.</Eyebrow>
        <SectionTitle>Permissioned identity across the institutions people already interact with.</SectionTitle>
        <Lede>
          VeraLok supports permissioned identity interactions across Hospitality,
          Enterprise Services, Health, Finance and Government.
        </Lede>
        <div className="mt-8 vlk-panel overflow-hidden">
          <img
            src="/marketing/five-super-institutions.png"
            alt="VeraLok five Super Institutions network"
            className="w-full h-auto block vlk-3d-float"
          />
        </div>
        <div className="mt-8">
          <CTA to="/institutions" label="Explore Institutions" testid="home-cta-explore-institutions" />
        </div>
      </section>

      <section className="border-t border-white/8 pt-16 relative grid md:grid-cols-2 gap-10 items-center">
        <div>
          <Eyebrow>Trust should be demonstrable.</Eyebrow>
          <SectionTitle>Outcomes, governance and responsible disclosure.</SectionTitle>
          <Lede>
            VeraLok combines identity control, privacy-conscious disclosure, accountable interactions
            and responsible security practices in a platform designed for real institutional use.
          </Lede>
          <div className="mt-8">
            <CTA to="/trust-security" label="Trust & Security" primary={false} testid="home-cta-trust" />
          </div>
        </div>
        <div className="vlk-panel overflow-hidden">
          <img src="/marketing/trust-security.png"
               alt="VeraLok public trust and security visual"
               className="w-full h-auto block vlk-3d-float" />
        </div>
      </section>

      <section className="border-t border-white/8 pt-16 relative">
        <Eyebrow>Built for identity interactions that matter.</Eyebrow>
        <SectionTitle>VeraLok Holdings Inc. develops identity-control infrastructure.</SectionTitle>
        <Lede>
          For individuals and institutions — one connected ecosystem with the individual at the center.
        </Lede>
        <div className="mt-8">
          <CTA to="/company" label="About VeraLok" primary={false} testid="home-cta-company" />
        </div>
      </section>
    </div>
  );
}

// Legacy alias — some routes/tests may still import Product.
export function Product() {
  return HowItWorks();
}

// ─── How VeraLok Works ─────────────────────────────────────────────────────
export function HowItWorks() {
  useDocumentMeta({
    title: "How VeraLok Works — Identity Control",
    description: "See how VeraLok supports verification, permissioned sharing and ongoing identity control.",
    canonicalPath: "/how-it-works",
  });
  const steps = [
    ["01", "Establish your VeraLok identity", "Complete the supported verification process to create your VeraLok identity."],
    ["02", "Receive a request",               "An institution requests the verified information required for the interaction."],
    ["03", "Review before sharing",           "See what is being requested and make the consent decision."],
    ["04", "Share the permitted result",      "The institution receives the identity result permitted for that interaction."],
    ["05", "Stay in control",                 "Retain visibility into identity activity and use VeraLok's available control actions when needed."],
  ];
  return (
    <div className="space-y-12 relative" data-testid="marketing-how">
      <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center">
        <div>
          <Eyebrow>How VeraLok works</Eyebrow>
          <SectionTitle level="h1">Verification with control built in.</SectionTitle>
          <Lede>
            VeraLok gives the individual a clear role throughout the identity interaction —
            from verification to disclosure and continued access control.
          </Lede>
        </div>
        <PadlockKeyHero testid="how-hero-visual" />
      </div>
      <ol className="space-y-4">
        {steps.map(([n, t, b]) => (
          <li key={n} className="grid grid-cols-[64px,1fr] gap-4 vlk-panel vlk-interactive p-6">
            <div className="font-mono text-[color:var(--vlk-cyan)] text-sm pt-1">{n}</div>
            <div>
              <div className="font-semibold text-lg">{t}</div>
              <div className="text-white/70 text-sm mt-1 leading-relaxed">{b}</div>
            </div>
          </li>
        ))}
      </ol>
      <section className="border-t border-white/8 pt-12">
        <Eyebrow>End-to-end sequence</Eyebrow>
        <SectionTitle>The full VeraLok interaction, at a glance.</SectionTitle>
        <div className="mt-8 vlk-panel overflow-hidden">
          <img src="/marketing/motion-storyboard.png"
               alt="VeraLok six-panel product motion storyboard — identity you control, institutional interaction, review and consent, permitted result, control, company"
               className="w-full h-auto block vlk-3d-float"
               loading="lazy" />
        </div>
      </section>
      <div className="vlk-panel p-8">
        <div className="text-2xl font-semibold" style={{ fontFamily: "var(--vlk-font-display)" }}>
          Identity should remain understandable before, during and after verification.
        </div>
        <div className="mt-6">
          <CTA to="/citizen" label="Get VeraLok" testid="how-cta-get-veralok" />
        </div>
      </div>
      <p className="text-xs text-white/40">
        Public-storytelling note: this page describes the user experience, not literal backend sequencing or security-control order.
      </p>
    </div>
  );
}

// ─── For Citizens (Get VeraLok landing) ────────────────────────────────────
export function ForCitizens() {
  useDocumentMeta({
    title: "Get VeraLok — Citizen App",
    description: "Download the VeraLok Citizen app to verify your identity, share deliberately and control access.",
    canonicalPath: "/citizen",
  });
  return (
    <div className="space-y-12 relative" data-testid="marketing-citizen">
      <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center">
        <div>
          <Eyebrow>VeraLok Citizen App</Eyebrow>
          <SectionTitle level="h1">Take control of your identity.</SectionTitle>
          <Lede>
            Verify, share and manage access to your identity with transparency, consent and control built into the interaction.
          </Lede>
        </div>
        <PadlockKeyHero testid="citizen-hero-visual" />
      </div>

      <div className="vlk-panel p-8" data-testid="citizen-download">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--vlk-cyan)] mb-3">
          Available now
        </div>
        <div className="text-lg text-white/85 leading-relaxed mb-6">
          VeraLok is a one-time paid download. No subscription, no in-app purchase.
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://apps.apple.com/app/veralok/id6754166160"
            className="vlk-btn-primary"
            data-testid="citizen-download-ios"
            aria-label="Download the VeraLok Citizen app for iOS"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for iOS
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.veralok.app"
            className="vlk-btn-primary"
            data-testid="citizen-download-android"
            aria-label="Download the VeraLok Citizen app for Android"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download for Android
          </a>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {[
          ["Verify",   "Complete the supported verification to create your VeraLok identity."],
          ["Consent",  "Review each institutional request before information is shared."],
          ["Control",  "Manage identity access and retain visibility into activity."],
        ].map(([t, b]) => <PillCard key={t} title={t} body={b} />)}
      </div>
    </div>
  );
}

// ─── For Institutions ─────────────────────────────────────────────────────
export function ForInstitutions() {
  useDocumentMeta({
    title: "VeraLok for Institutions",
    description: "Permissioned verified identity interactions across Hospitality, Enterprise Services, Health, Finance and Government.",
    canonicalPath: "/institutions",
  });
  const gateways = [
    ["Hospitality",
     "Verified identity for guest, property, venue and membership interactions.",
     "/hospitality"],
    ["Enterprise Services",
     "Identity control across education and training, telecommunications, travel, aviation, workforce, technology, professional services and mobility environments.",
     "/enterprise-services"],
    ["Health",
     "Trusted identity interactions across care, pharmacy, diagnostics, dental, telehealth and long-term/home-care environments.",
     "/health"],
    ["Finance",
     "Permissioned identity verification across banking, fintech, payments, insurance, lending, investment and financial infrastructure.",
     "/finance"],
    ["Government",
     "Citizen-controlled identity interactions across public identity, immigration, regulatory, licensing, benefits, tax and government-service environments.",
     "/government"],
  ];
  const path = [
    ["Understand",  "Define the institutional interaction and current friction."],
    ["Demonstrate", "Review the relevant VeraLok workflow."],
    ["Qualify",     "Confirm scope, readiness, integration needs and decision criteria."],
    ["Pilot",       "Where appropriate, run a tightly scoped 30-day institutional pilot."],
    ["Evaluate",    "Review evidence, operating fit and next-step commercial structure."],
  ];
  return (
    <div className="space-y-16 relative" data-testid="marketing-institutions">
      <div>
        <Eyebrow>Institutions</Eyebrow>
        <SectionTitle level="h1">Identity verification with the individual still in control.</SectionTitle>
        <Lede>
          Institutions can request verified identity information through permissioned VeraLok interactions.
          The individual reviews the request, approves the permitted disclosure, and the institution
          receives the authorized verified result.
        </Lede>
        <div className="mt-8">
          <CTA to="/engage" label="Engage VeraLok" testid="institutions-cta-engage" />
        </div>
      </div>

      <section>
        <Eyebrow>One VeraLok institutional architecture</Eyebrow>
        <SectionTitle>Five environments. One identity-control model.</SectionTitle>
        <Lede>
          The same identity-control model supports different institutional environments
          without turning VeraLok into separate products for each sector.
        </Lede>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {gateways.map(([t, b, href]) => (
            <Link key={t} to={href}
                  className="vlk-panel vlk-interactive vlk-3d-tilt overflow-hidden block"
                  data-testid={`institutions-card-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <img
                src={INSTITUTION_IMAGES[t]}
                alt={`VeraLok for ${t} — sector-specific 3D scene with padlock and key motif`}
                className="w-full h-auto block aspect-[16/9] object-cover object-center"
                loading="lazy"
              />
              <div className="p-6">
                <div className="text-xl font-semibold">{t}</div>
                <div className="text-white/60 text-sm mt-3 leading-relaxed">{b}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-white/8 pt-16">
        <Eyebrow>Engagement</Eyebrow>
        <SectionTitle>A clear path from interest to production learning.</SectionTitle>
        <div className="mt-8 grid md:grid-cols-5 gap-4">
          {path.map(([t, b], i) => (
            <div key={t} className="vlk-panel p-5">
              <div className="font-mono text-[color:var(--vlk-cyan)] text-xs">0{i + 1}</div>
              <div className="mt-2 font-semibold">{t}</div>
              <div className="mt-2 text-white/60 text-xs leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <CTA to="/engage" label="Request an Institutional Conversation" testid="institutions-cta-request" />
        </div>
      </section>
    </div>
  );
}

// ─── Super-Institution subpages (shared template) ─────────────────────────
function SuperInstitutionPage({ testid, title, subtitle, hero, description, includes, outcomes, canonicalPath, seoTitle, seoDesc }) {
  useDocumentMeta({
    title: seoTitle,
    description: seoDesc,
    canonicalPath,
  });
  const image = INSTITUTION_IMAGES[title];
  return (
    <div className="space-y-12 relative" data-testid={testid}>
      {/*
        Full-width premium 3D hero visual for the sector. The
        composition carries the VeraLok padlock + key motif alongside
        the sector's 3D architectural scene. The HTML H1/lede/CTA
        below the image are retained for accessibility and search
        engines (each per-route prerendered index.html also carries
        these as <noscript> content).
      */}
      {image && (
        <div className="vlk-panel overflow-hidden">
          <img
            src={image}
            alt={`VeraLok for ${title} — sector-specific 3D scene with padlock and key motif`}
            className="w-full h-auto block"
          />
        </div>
      )}
      <div>
        <Eyebrow>{subtitle}</Eyebrow>
        <SectionTitle level="h1">{hero}</SectionTitle>
        <Lede>{description}</Lede>
        <div className="mt-8">
          <CTA to="/engage" label="Engage VeraLok" testid={`${testid}-cta`} />
        </div>
      </div>
      <section>
        <Eyebrow>Where it fits</Eyebrow>
        <SectionTitle>{title} includes</SectionTitle>
        <ul className="mt-6 grid md:grid-cols-2 gap-3">
          {includes.map((s) => (
            <li key={s} className="vlk-panel p-4 text-sm text-white/80">{s}</li>
          ))}
        </ul>
      </section>
      <section className="border-t border-white/8 pt-12">
        <Eyebrow>Experience</Eyebrow>
        <SectionTitle>A cleaner identity interaction for both sides.</SectionTitle>
        <Lede>
          The institution requests the verified identity result required for the interaction.
          The individual reviews the request and approves the permitted disclosure.
        </Lede>
      </section>
      <section className="border-t border-white/8 pt-12">
        <Eyebrow>Outcomes</Eyebrow>
        <ul className="mt-4 grid md:grid-cols-2 gap-3">
          {outcomes.map((o) => (
            <li key={o} className="vlk-panel p-4 text-sm text-white/80">{o}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
export function Hospitality() {
  return <SuperInstitutionPage
    testid="marketing-hospitality"
    title="Hospitality"
    subtitle="Hospitality"
    seoTitle="VeraLok for Hospitality"
    seoDesc="Verified identity interactions for hospitality, property, venues and membership environments."
    canonicalPath="/hospitality"
    hero="Verified identity for guest interactions."
    description="Let people verify once and share only what the interaction requires."
    includes={[
      "Hotels & Resorts",
      "Serviced & Short-Stay Accommodation",
      "Property Management & Real Estate Services",
      "Events & Venues",
      "Membership Organizations & Associations",
    ]}
    outcomes={[
      "clearer identity requests",
      "permissioned verified results",
      "less unnecessary identity-data handling",
      "auditable interaction history",
    ]}
  />;
}
export function EnterpriseServices() {
  return <SuperInstitutionPage
    testid="marketing-enterprise-services"
    title="Enterprise Services"
    subtitle="Enterprise Services"
    seoTitle="VeraLok for Enterprise Services"
    seoDesc="Identity control across education, workforce, technology, travel, professional and mobility environments."
    canonicalPath="/enterprise-services"
    hero="Identity control across modern organizations."
    description="Support trusted identity interactions across workforce, technology, travel, professional and other enterprise environments."
    includes={[
      "Education & Training Institutions",
      "Telecommunications",
      "Travel Services",
      "Aviation",
      "Employers & Workforce",
      "Technology & Digital Services",
      "Professional Services",
      "Transportation & Mobility",
    ]}
    outcomes={[
      "permissioned verification",
      "consistent identity experience",
      "clear request context",
      "auditable institutional interaction",
    ]}
  />;
}
export function Health() {
  return <SuperInstitutionPage
    testid="marketing-health"
    title="Health"
    subtitle="Health"
    seoTitle="VeraLok for Health"
    seoDesc="Trusted identity verification and permissioned disclosure across health environments."
    canonicalPath="/health"
    hero="Trusted identity for care interactions."
    description="Support clear identity verification and permissioned information sharing across health environments."
    includes={[
      "Hospitals & Health Systems",
      "Clinics & Medical Practices",
      "Pharmacies",
      "Diagnostic & Laboratory Services",
      "Dental & Oral Health",
      "Telehealth & Digital Health",
      "Long-Term & Home Care",
    ]}
    outcomes={[
      "consent before disclosure",
      "verified identity outcomes",
      "reduced unnecessary data exposure",
      "clearer institutional accountability",
    ]}
  />;
}
export function Finance() {
  return <SuperInstitutionPage
    testid="marketing-finance"
    title="Finance"
    subtitle="Finance"
    seoTitle="VeraLok for Finance"
    seoDesc="Permissioned verified identity interactions for financial institutions and services."
    canonicalPath="/finance"
    hero="Identity verification with the individual still in control."
    description="Enable institutions to request verified identity information through permissioned VeraLok interactions."
    includes={[
      "Banks",
      "Fintechs & Digital Finance",
      "Microfinance & Cooperative Finance",
      "Payments & Remittance",
      "Insurance",
      "Lending & Credit",
      "Investment & Wealth Management",
      "Capital Markets & Financial Infrastructure",
    ]}
    outcomes={[
      "verified identity requests",
      "controlled disclosure",
      "clear interaction context",
      "auditable activity",
    ]}
  />;
}
export function Government() {
  return <SuperInstitutionPage
    testid="marketing-government"
    title="Government"
    subtitle="Government"
    seoTitle="VeraLok for Government"
    seoDesc="Citizen-controlled identity interactions for public services and government environments."
    canonicalPath="/government"
    hero="Citizen-controlled identity for public-service interactions."
    description="Support trusted identity requests while keeping disclosure visible and permissioned."
    includes={[
      "Identity & Civil Registration",
      "Immigration & Border Services",
      "Regulators & Supervisory Authorities",
      "National / Federal Government",
      "State / Regional Government",
      "Local / Municipal Government",
      "Licensing & Permitting Authorities",
      "Public Benefits & Social Services",
      "Tax & Revenue Authorities",
    ]}
    outcomes={[
      "clear requests",
      "citizen consent",
      "verified identity outcomes",
      "auditable interaction",
    ]}
  />;
}

// ─── Developers ───────────────────────────────────────────────────────────
export function Developers() {
  useDocumentMeta({
    title: "VeraLok Developers",
    description: "Developer resources for integrating supported VeraLok identity and institutional workflows.",
    canonicalPath: "/developers",
  });
  return (
    <div className="space-y-12 relative" data-testid="marketing-developers">
      <div>
        <Eyebrow>Developers</Eyebrow>
          <SectionTitle level="h1">Build identity interactions around permission, not guesswork.</SectionTitle>
          <Lede>
            Use VeraLok developer capabilities to integrate supported identity-verification and
            institutional workflows into your applications.
          </Lede>
          <div className="mt-8">
            <a href="https://developer.veralok.com" className="vlk-btn-primary"
               data-testid="developers-cta-portal" target="_blank" rel="noopener noreferrer">
              Developer Portal
            </a>
          </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {[
          ["Clear authorization",  "Use supported authentication and authorization patterns for VeraLok integrations."],
          ["Scoped access",        "Request only the capabilities and identity outcomes required for the integration."],
          ["Verifiable events",    "Use supported event and webhook capabilities to keep integrated systems synchronized."],
          ["Production discipline", "Treat identity integration as a production trust boundary, not a convenience API."],
        ].map(([t, b]) => <PillCard key={t} title={t} body={b} />)}
      </div>
      <div className="vlk-panel p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--vlk-cyan)] mb-3">Documentation</div>
        <p className="text-white/70 text-sm leading-relaxed">
          The Developer Portal is the authority for current endpoints, authentication, scopes,
          webhook behavior and integration guidance. Sensitive implementation details are not
          reproduced on this marketing site.
        </p>
        <div className="mt-6">
          <a href="https://developer.veralok.com" className="vlk-btn-ghost"
             target="_blank" rel="noopener noreferrer" data-testid="developers-cta-open-portal">
            Open Developer Portal
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Trust & Security ─────────────────────────────────────────────────────
export function TrustSecurity() {
  useDocumentMeta({
    title: "VeraLok Trust & Security",
    description: "Learn about VeraLok's public security, privacy, responsible disclosure and institutional trust posture.",
    canonicalPath: "/trust-security",
  });
  const rows = [
    ["Identity protection",         "VeraLok is designed around layered identity protection and controlled institutional interaction."],
    ["Privacy-conscious disclosure","Identity information should be requested for a clear purpose and disclosed through the supported permissioned workflow."],
    ["Accountability",              "Supported identity interactions retain an auditable record appropriate to the VeraLok experience."],
    ["Platform operations",         "VeraLok applies production security, monitoring and operational controls appropriate to an identity platform."],
    ["Framework discipline",        "VeraLok maps implemented controls against recognized security and privacy control frameworks to support institutional review and continuous improvement. Independent certifications, attestations or audits are identified only after they actually exist."],
  ];
  return (
    <div className="space-y-12 relative" data-testid="marketing-trust-security">
      <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center">
        <div>
          <Eyebrow>Trust &amp; Security</Eyebrow>
          <SectionTitle level="h1">Trust should be demonstrable.</SectionTitle>
          <Lede>
            VeraLok communicates security and privacy through outcomes, governance and
            responsible disclosure — not proprietary implementation detail.
          </Lede>
        </div>
        <div className="vlk-panel overflow-hidden">
          <img src="/marketing/trust-security.png" alt="VeraLok trust and security" className="w-full h-auto block vlk-3d-float" />
        </div>
      </div>
      <div className="vlk-panel divide-y divide-white/8">
        {rows.map(([k, v]) => (
          <div key={k} className="grid md:grid-cols-[280px,1fr] p-5 gap-2 md:gap-6">
            <div className="font-semibold text-sm">{k}</div>
            <div className="text-white/70 text-sm leading-relaxed">{v}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="vlk-panel p-6">
          <div className="text-lg font-semibold">Responsible disclosure</div>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            Security researchers and institutions can report security concerns through VeraLok's designated security channel.
          </p>
          <div className="mt-4">
            <CTA to="/security-report" label="Report a Security Concern" primary={false} testid="trust-cta-security-report" />
          </div>
        </div>
        <div className="vlk-panel p-6">
          <div className="text-lg font-semibold">Availability</div>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            For current service availability and incident communication, visit VeraLok Status.
          </p>
          <div className="mt-4">
            <CTA to="/status" label="View Status" primary={false} testid="trust-cta-status" />
          </div>
        </div>
        <div className="vlk-panel p-6">
          <div className="text-lg font-semibold">Institutional diligence</div>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            Institutions with a qualified diligence need can request appropriate additional
            trust/security information through the institutional engagement process.
          </p>
          <div className="mt-4">
            <CTA to="/engage" label="Institutional Inquiry" primary={false} testid="trust-cta-inquiry" />
          </div>
        </div>
      </div>
      <p className="text-xs text-white/40">
        Proprietary controls, internal thresholds, scoring logic, enforcement semantics and detailed infrastructure remain private.
      </p>
    </div>
  );
}
// Legacy alias used by App.js imports (Security).
export function Security() {
  return TrustSecurity();
}

// ─── Company ──────────────────────────────────────────────────────────────
export function Company() {
  useDocumentMeta({
    title: "VeraLok Holdings Inc.",
    description: "Learn about VeraLok Holdings Inc. and its identity-control mission.",
    canonicalPath: "/company",
  });
  return (
    <div className="space-y-12 relative" data-testid="marketing-company">
      <div>
        <Eyebrow>Company</Eyebrow>
          <SectionTitle level="h1">Identity infrastructure built around control.</SectionTitle>
          <Lede>
            VeraLok Holdings Inc. develops identity-control infrastructure that helps individuals
            and institutions interact with verified identity more clearly.
          </Lede>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <PillCard title="What VeraLok does"
                  body="VeraLok gives individuals a practical control layer around verified identity while giving institutions a permissioned way to request verified identity outcomes." />
        <PillCard title="One connected ecosystem"
                  body="The VeraLok ecosystem includes distinct citizen, institutional, developer and operational surfaces connected by shared identity, consent and audit principles." />
      </div>
      <div className="vlk-panel p-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--vlk-cyan)] mb-3">Corporate</div>
        <div className="text-lg font-semibold">VeraLok Holdings Inc.</div>
        <address className="not-italic text-white/70 text-sm mt-2 leading-relaxed">
          251 Little Falls Drive<br />
          Wilmington, Delaware 19808<br />
          United States
        </address>
      </div>
      <div>
        <CTA to="/leadership" label="Meet the Leadership" testid="company-cta-leadership" />
      </div>
    </div>
  );
}
// Legacy alias used by App.js imports.
export function About() { return Company(); }

// ─── Leadership ────────────────────────────────────────────────────────────
const LEADERS = [
  { name: "McOwen M. Farrell",           role: "Chief Executive Officer",
    photo: "/leadership/mcowen-farrell.jpg", focal: "50% 18%",
    bio: "Leads VeraLok's company strategy, institutional direction and executive coordination, aligning product, trust, commercial and operating priorities around the company's identity-control mission." },
  { name: "Eduardo S. Souza",            role: "Chief Product & Experience Officer",
    photo: "/leadership/eduardo-souza.jpg", focal: "50% 22%",
    bio: "Leads product strategy and experience across VeraLok, shaping coherent citizen, institutional and developer journeys across the connected ecosystem." },
  { name: "Murray G. Morris",            role: "Chief Operating Officer",
    photo: "/leadership/murray-morris.jpg", focal: "50% 18%",
    bio: "Leads operating discipline and cross-functional delivery, translating company priorities into coordinated execution and accountable operating systems." },
  { name: "Noma Itaman",                 role: "Principal Architect & Chairman, Tech Steering Committee",
    photo: "/leadership/noma-itaman.jpg", focal: "50% 25%",
    bio: "Guides VeraLok's architectural direction and chairs the Tech Steering Committee, maintaining technical coherence and long-term integrity across the platform." },
  { name: "Brandon R. McDonald",         role: "Chief Commercial Officer",
    photo: "/leadership/brandon-mcdonald.jpg", focal: "50% 20%",
    bio: "Leads institutional growth, partnerships and commercial execution, advancing qualified opportunities from engagement through pilots, proposals and long-term relationships." },
  { name: "Oluwaseun Ademide Bamidele",  role: "Chief Privacy & Legal Officer",
    photo: "/leadership/oluwaseun-bamidele.jpg", focal: "50% 22%",
    bio: "Leads privacy, legal coordination and data governance, supporting responsible growth, institutional diligence and trusted identity interactions." },
  { name: "Maya Ana Schueller",          role: "Chief Security & Trust Officer",
    photo: "/leadership/maya-schueller.jpg", focal: "50% 20%",
    bio: "Leads VeraLok's security and trust posture, overseeing security governance, institutional assurance and the public trust standards that support adoption." },
];
export function Leadership() {
  useDocumentMeta({
    title: "VeraLok Leadership",
    description: "Meet the executive leadership of VeraLok Holdings Inc.",
    canonicalPath: "/leadership",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "VeraLok Holdings Inc.",
      url: "https://www.veralok.com",
      employee: LEADERS.map((l) => ({
        "@type": "Person",
        name: l.name,
        jobTitle: l.role,
      })),
    },
  });
  return (
    <div className="space-y-12 relative" data-testid="marketing-leadership">
      <div>
        <Eyebrow>Executive leadership</Eyebrow>
        <SectionTitle level="h1">People behind VeraLok.</SectionTitle>
        <Lede>
          VeraLok's leadership spans company strategy, product, operations, architecture,
          commercial execution, privacy/legal and security/trust.
        </Lede>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {LEADERS.map((l) => (
          <div key={l.name} className="vlk-panel vlk-interactive overflow-hidden"
               data-testid={`leadership-card-${l.name.split(" ")[0].toLowerCase()}`}>
            <div className="w-full bg-[color:var(--vlk-panel)]"
                 style={{ aspectRatio: "3 / 4" }}>
              <img src={l.photo} alt={l.name}
                   className="w-full h-full object-cover"
                   style={{ objectPosition: l.focal || "50% 20%" }}
                   loading="lazy" />
            </div>
            <div className="p-5">
              <div className="text-lg font-semibold">{l.name}</div>
              <div className="text-[color:var(--vlk-cyan)] text-sm mt-1">{l.role}</div>
              <p className="text-white/70 text-sm mt-3 leading-relaxed">{l.bio}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Engage VeraLok ────────────────────────────────────────────────────────
export function Engage() {
  useDocumentMeta({
    title: "Engage VeraLok",
    description: "Start an institutional conversation, demo or qualified pilot discussion with VeraLok.",
    canonicalPath: "/engage",
  });
  const steps = [
    ["Understand",  "Define the institutional interaction and current friction."],
    ["Demonstrate", "Review the relevant VeraLok workflow."],
    ["Qualify",     "Confirm scope, readiness, integration needs and decision criteria."],
    ["Pilot",       "Where appropriate, run a tightly scoped 30-day institutional pilot."],
    ["Evaluate",    "Review evidence, operating fit and next-step commercial structure."],
  ];
  return (
    <div className="space-y-12 relative" data-testid="marketing-engage">
      <div>
        <Eyebrow>Engage VeraLok</Eyebrow>
          <SectionTitle level="h1">Start with the institutional outcome.</SectionTitle>
          <Lede>
            Tell VeraLok what identity interaction you need to improve. The conversation starts with
            the use case, not a generic platform pitch.
          </Lede>
      </div>
      <ol className="grid md:grid-cols-5 gap-4">
        {steps.map(([t, b], i) => (
          <li key={t} className="vlk-panel p-5">
            <div className="font-mono text-[color:var(--vlk-cyan)] text-xs">0{i + 1}</div>
            <div className="mt-2 font-semibold">{t}</div>
            <div className="mt-2 text-white/60 text-xs leading-relaxed">{b}</div>
          </li>
        ))}
      </ol>
      <div className="vlk-panel p-8">
        <div className="text-2xl font-semibold" style={{ fontFamily: "var(--vlk-font-display)" }}>
          Start an Institutional Conversation
        </div>
        <p className="text-white/70 text-sm mt-3 leading-relaxed">
          Share your use case and we will route your request to the right team.
        </p>
        <div className="mt-6 flex gap-3">
          <CTA to="/request-demo" label="Request a demo" testid="engage-cta-demo" />
          <CTA to="/contact" label="Contact VeraLok" primary={false} testid="engage-cta-contact" />
        </div>
      </div>
    </div>
  );
}

// ─── Resources ─────────────────────────────────────────────────────────────
export function Resources() {
  useDocumentMeta({
    title: "VeraLok Resources",
    description: "Company, product, institutional and trust resources from VeraLok.",
    canonicalPath: "/resources",
  });
  const cats = [
    ["Company",        "Approved company description, leadership and corporate information.",   "/company"],
    ["Product",        "Current VeraLok product explanations, screenshots and demonstrations.",  "/how-it-works"],
    ["Institutions",   "Approved institutional use-case material organized by the five Super Institutions.", "/institutions"],
    ["Trust",          "Privacy, Trust & Security, responsible disclosure and status resources.", "/trust-security"],
    ["Media",          "Approved logo assets, screenshots and press contact.",                   "/contact"],
  ];
  return (
    <div className="space-y-12 relative" data-testid="marketing-resources">
      <div>
        <Eyebrow>Resources / Media</Eyebrow>
          <SectionTitle level="h1">VeraLok resources.</SectionTitle>
          <Lede>Company information, approved product material, demonstrations and public trust resources.</Lede>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cats.map(([t, b, href]) => (
          <Link key={t} to={href} className="vlk-panel vlk-interactive p-6 block"
                data-testid={`resources-card-${t.toLowerCase()}`}>
            <div className="text-lg font-semibold">{t}</div>
            <div className="text-white/60 text-sm mt-2 leading-relaxed">{b}</div>
          </Link>
        ))}
      </div>
      <p className="text-xs text-white/50">
        Press contact: <a href="mailto:press@veralok.com" className="underline hover:text-white">press@veralok.com</a>.
      </p>
    </div>
  );
}

// ─── Status ────────────────────────────────────────────────────────────────
export function Status() {
  useDocumentMeta({
    title: "VeraLok Status",
    description: "Current VeraLok service availability and incident communication.",
    canonicalPath: "/status",
  });
  const [health, setHealth] = useState({ state: "loading", detail: "" });
  useEffect(() => {
    let cancelled = false;
    api.get("/health").then((r) => {
      if (cancelled) return;
      const ok = r?.data?.status === "healthy" && r?.data?.db === "connected";
      setHealth({ state: ok ? "operational" : "degraded", detail: JSON.stringify(r?.data || {}) });
    }).catch((e) => {
      if (cancelled) return;
      setHealth({ state: "unreachable", detail: e?.message || "unreachable" });
    });
    return () => { cancelled = true; };
  }, []);
  const colour = health.state === "operational" ? "#22c55e"
    : health.state === "loading" ? "#94a3b8"
    : health.state === "degraded" ? "#f59e0b" : "#ef4444";
  return (
    <div className="space-y-12 relative" data-testid="marketing-status">
      <div>
        <Eyebrow>Status</Eyebrow>
          <SectionTitle level="h1">VeraLok Status.</SectionTitle>
          <Lede>Current availability and service communications for VeraLok production services.</Lede>
      </div>
      <div className="vlk-panel p-6" data-testid="status-api">
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: colour }} aria-hidden="true" />
          <div className="font-semibold">Public API — api.veralok.com</div>
        </div>
        <div className="text-white/60 text-sm mt-2">
          {health.state === "loading" && "Checking service health…"}
          {health.state === "operational" && "Operational. Latest health probe successful."}
          {health.state === "degraded" && "Degraded. Health probe returned an unexpected state."}
          {health.state === "unreachable" && "Unreachable from this browser. Reflects the current probe only."}
        </div>
      </div>
      <p className="text-xs text-white/40">
        This page reflects a live health probe against the VeraLok API from your browser.
        Incident communications are posted here when service state changes.
      </p>
    </div>
  );
}

// ─── Security Reporting ───────────────────────────────────────────────────
export function SecurityReport() {
  useDocumentMeta({
    title: "VeraLok Security Reporting",
    description: "Report suspected security vulnerabilities or security-related concerns to VeraLok.",
    canonicalPath: "/security-report",
  });
  const [form, setForm] = useState({ reporter_email: "", summary: "", description: "", affected_surface: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Reuse the marketing contact intake; category=Security.
      const r = await api.post("/marketing/contact", {
        name: "Security Reporter",
        email: form.reporter_email,
        company: "",
        subject: `[SECURITY] ${form.summary}`,
        message: `Affected surface: ${form.affected_surface}\n\n${form.description}`,
        category: "Security",
      });
      toast.success("Security report received", { description: `Ref: ${r.data.lead_id}` });
      setForm({ reporter_email: "", summary: "", description: "", affected_surface: "" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Submit failed", { description: typeof d === "string" ? d : d?.reason || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl space-y-8 relative" data-testid="marketing-security-report">
      <div>
        <Eyebrow>Report a security concern</Eyebrow>
        <SectionTitle level="h1">Responsible disclosure.</SectionTitle>
        <Lede>
          VeraLok welcomes responsible reports that help protect the platform and its users.
          Do not use this page for general product support.
        </Lede>
      </div>
      <form onSubmit={submit} className="vlk-panel p-6 space-y-4" data-testid="security-report-form">
        <Field label="Reporter email" type="email" value={form.reporter_email}
               onChange={(v) => setForm({ ...form, reporter_email: v })}
               testid="security-report-email" required />
        <Field label="Summary" value={form.summary}
               onChange={(v) => setForm({ ...form, summary: v })}
               testid="security-report-summary" required />
        <Field label="Affected VeraLok surface" value={form.affected_surface}
               onChange={(v) => setForm({ ...form, affected_surface: v })}
               testid="security-report-surface" />
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Description</label>
          <textarea rows={6} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded p-3 text-sm text-white outline-none focus:border-[color:var(--vlk-cyan)]"
                    data-testid="security-report-description" required />
        </div>
        <button type="submit" disabled={busy} className="vlk-btn-primary disabled:opacity-50"
                data-testid="security-report-submit">
          {busy ? "Submitting…" : "Submit report"}
        </button>
      </form>
      <p className="text-xs text-white/50">
        Alternate channel once routing is live: <a href="mailto:security@veralok.com" className="underline hover:text-white">security@veralok.com</a>.
      </p>
    </div>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────
const CONTACT_CATEGORIES = [
  "General",
  "Institutional / Demo",
  "Partnerships",
  "Developer / Integration",
  "Privacy",
  "Security",
  "Media / Press",
];
export function Contact() {
  useDocumentMeta({
    title: "Contact VeraLok",
    description: "Contact VeraLok for institutional, partnership, developer, privacy, security or media inquiries.",
    canonicalPath: "/contact",
  });
  const [form, setForm] = useState({
    name: "", email: "", company: "", country: "",
    category: "General", subject: "", message: "", acknowledged: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged) {
      toast.error("Please acknowledge the privacy note before submitting.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, email: form.email, company: form.company,
        subject: `[${form.category}] ${form.subject}`,
        message: `${form.country ? "Country: " + form.country + "\n\n" : ""}${form.message}`,
        category: form.category,
      };
      const r = await api.post("/marketing/contact", payload);
      toast.success("Your message has been received by VeraLok.", {
        description: `Ref: ${r.data.lead_id}. The appropriate team will respond through the contact details you provided.`,
      });
      setForm({ name: "", email: "", company: "", country: "",
                category: "General", subject: "", message: "", acknowledged: false });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Send failed", { description: typeof d === "string" ? d : d?.reason || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl space-y-8 relative" data-testid="marketing-contact">
      <div>
        <Eyebrow>Contact</Eyebrow>
        <SectionTitle level="h1">Contact VeraLok.</SectionTitle>
        <Lede>Choose the reason for your message so it reaches the right team.</Lede>
      </div>
      <form onSubmit={submit} className="vlk-panel p-6 space-y-4" data-testid="contact-form">
        <Field label="Full name" value={form.name}
               onChange={(v) => setForm({ ...form, name: v })}
               testid="contact-name" required />
        <Field label="Email" type="email" value={form.email}
               onChange={(v) => setForm({ ...form, email: v })}
               testid="contact-email" required />
        <Field label="Organization" value={form.company}
               onChange={(v) => setForm({ ...form, company: v })}
               testid="contact-company" />
        <Field label="Country / region" value={form.country}
               onChange={(v) => setForm({ ...form, country: v })}
               testid="contact-country" />
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Inquiry type</label>
          <select value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white outline-none focus:border-[color:var(--vlk-cyan)]"
                  data-testid="contact-category">
            {CONTACT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="Subject" value={form.subject}
               onChange={(v) => setForm({ ...form, subject: v })}
               testid="contact-subject" required />
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Message</label>
          <textarea rows={5} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded p-3 text-sm text-white outline-none focus:border-[color:var(--vlk-cyan)]"
                    data-testid="contact-message" required />
        </div>
        <label className="flex items-start gap-2 text-xs text-white/60" data-testid="contact-privacy-ack">
          <input type="checkbox" checked={form.acknowledged}
                 onChange={(e) => setForm({ ...form, acknowledged: e.target.checked })}
                 className="mt-1" />
          <span>
            I acknowledge that VeraLok processes contact form submissions in accordance with the{" "}
            <Link to="/legal/privacy" className="underline hover:text-white">Privacy Policy</Link>.
          </span>
        </label>
        <button type="submit" disabled={busy} className="vlk-btn-primary disabled:opacity-50"
                data-testid="contact-submit">
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
      <p className="text-xs text-white/50">
        VeraLok does not display a general public phone number. All contact is form-first.
      </p>
    </div>
  );
}
function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
             className="mt-1 w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white outline-none focus:border-[color:var(--vlk-cyan)]"
             data-testid={testid} />
    </div>
  );
}

// ─── FAQ (retained for backwards-compat routing) ──────────────────────────
export function FAQ() {
  useDocumentMeta({
    title: "VeraLok — Frequently Asked Questions",
    description: "Frequently asked questions about VeraLok, its identity control model and institutional interactions.",
    canonicalPath: "/faq",
  });
  const qs = [
    ["What does VeraLok do?",
     "VeraLok gives people a practical control layer around verified identity. Individuals verify, review institutional requests and manage access. Institutions receive the verified result permitted for the interaction through permissioned workflows."],
    ["Who owns my identity information?",
     "Your consent decisions are yours. VeraLok's approach centers on individual control over disclosure, with auditable interactions and clear consent."],
    ["Where does VeraLok fit in institutional workflows?",
     "VeraLok is used by institutions in Hospitality, Enterprise Services, Health, Finance and Government. The same identity-control model supports different sectors without turning VeraLok into separate products."],
    ["How do I report a security concern?",
     "Use the Security Reporting page. Security submissions are routed separately from general support."],
    ["Is the VeraLok Citizen App a subscription?",
     "No. The Citizen App is a one-time paid download. There is currently no subscription, no VeraLok Plus, and no in-app purchase."],
  ];
  return (
    <div className="space-y-10 relative" data-testid="marketing-faq">
      <div>
        <Eyebrow>Frequently asked</Eyebrow>
        <SectionTitle level="h1">Answers to common VeraLok questions.</SectionTitle>
      </div>
      <div className="space-y-4">
        {qs.map(([q, a]) => (
          <div key={q} className="vlk-panel p-5">
            <div className="font-semibold">{q}</div>
            <div className="text-white/70 text-sm mt-2 leading-relaxed">{a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Request Demo ─────────────────────────────────────────────────────────
export function RequestDemo() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", country: "",
    use_case: "KYC", estimated_monthly_volume: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.estimated_monthly_volume === "") delete payload.estimated_monthly_volume;
      else payload.estimated_monthly_volume = parseInt(payload.estimated_monthly_volume, 10);
      const r = await api.post("/marketing/demo-request", payload);
      setOk(r.data.lead_id);
      toast.success("Demo request received", { description: `Ref: ${r.data.lead_id}` });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Submit failed", { description: typeof d === "string" ? d : d?.reason || e.message });
    } finally { setBusy(false); }
  };
  if (ok) {
    return (
      <div className="max-w-lg space-y-4" data-testid="marketing-request-demo-ok">
        <h1 className="text-4xl font-semibold tracking-tight">Received.</h1>
        <p className="text-white/60">
          We'll reach out within one business day. Reference: <code className="font-mono text-white/90">{ok}</code>.
        </p>
        <Link to="/" className="inline-block mt-4 vlk-btn-ghost">Back to home</Link>
      </div>
    );
  }
  return (
    <div className="max-w-xl space-y-6 relative" data-testid="marketing-request-demo">
      <div className="vlk-glow-orb-blue" style={{ top: "-100px", right: "-120px" }} aria-hidden="true" />
      <h1 className="text-4xl font-semibold tracking-tight vlk-reveal">Request a demo</h1>
      <p className="text-white/60 vlk-reveal d1">
        Fifteen minutes. Live guided walkthrough on isolated evaluation data. We tailor it to your use case.
      </p>
      <form onSubmit={submit} className="space-y-4 vlk-glass p-6 relative" data-testid="demo-form">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="demo-name" required />
        <Field label="Work email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="demo-email" required />
        <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="demo-company" required />
        <Field label="Your role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="demo-role" />
        <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="demo-country" />
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Primary use case</label>
          <select value={form.use_case} onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white outline-none focus:border-[color:var(--vlk-brand-400)]"
                  data-testid="demo-use-case">
            <option value="KYC">KYC / onboarding</option>
            <option value="AGE_VERIFICATION">Age verification</option>
            <option value="COMPLIANCE">Ongoing compliance</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <Field label="Estimated monthly verifications" type="number"
               value={form.estimated_monthly_volume}
               onChange={(v) => setForm({ ...form, estimated_monthly_volume: v })}
               testid="demo-volume" />
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Anything else we should know</label>
          <textarea rows={4} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded p-3 text-sm text-white outline-none focus:border-[color:var(--vlk-brand-400)]"
                    data-testid="demo-message" />
        </div>
        <button type="submit" disabled={busy} className="vlk-btn-primary disabled:opacity-50" data-testid="demo-submit">
          {busy ? "Submitting…" : "Request demo"}
        </button>
      </form>
    </div>
  );
}

// ─── Legal boilerplate ────────────────────────────────────────────────────
function LegalPage({ title, testid, children }) {
  return (
    <div className="max-w-3xl space-y-6" data-testid={testid}>
      <h1 className="text-4xl font-semibold tracking-tight vlk-reveal">{title}</h1>
      <div className="prose prose-invert prose-neutral text-white/80 text-sm leading-relaxed vlk-reveal d1">
        {children}
      </div>
    </div>
  );
}

export function Privacy() {
  const S = (id, num, title) => (
    <h2 id={id} className="text-lg font-semibold text-white/90 mt-8 scroll-mt-24">
      {num}. {title}
    </h2>
  );
  const SS = (title) => (
    <h3 className="text-base font-semibold text-white/85 mt-5">{title}</h3>
  );
  return (
    <LegalPage title="Privacy Policy" testid="legal-privacy">
      <p>
        <strong>Effective:</strong> 8 February 2026 &nbsp;·&nbsp;
        <strong>Version:</strong> 2.0
      </p>
      <p>
        <strong>Data controller:</strong> VeraLok Holdings, Inc.,
        New Castle County, Delaware, United States. &nbsp;·&nbsp;
        <strong>Privacy contact:</strong>{" "}
        <a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>
      </p>

      {/* Table of contents — anchor navigation for a long document, using the
          existing prose styling so it stays inside the design system. */}
      <nav
        aria-label="Table of contents"
        className="mt-6 mb-4 rounded-lg border border-white/10 bg-white/[0.03] p-4"
        data-testid="privacy-toc"
      >
        <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-3">
          Contents
        </div>
        <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-white/75 text-sm list-decimal list-inside marker:text-white/40">
          <li><a href="#overview" className="underline hover:text-white">Overview and about VeraLok</a></li>
          <li><a href="#collect" className="underline hover:text-white">Information VeraLok collects</a></li>
          <li><a href="#not-collect" className="underline hover:text-white">Information VeraLok does not collect</a></li>
          <li><a href="#use" className="underline hover:text-white">How VeraLok uses this information</a></li>
          <li><a href="#verification" className="underline hover:text-white">How identity verification works</a></li>
          <li><a href="#providers" className="underline hover:text-white">Service providers and processors</a></li>
          <li><a href="#cookies" className="underline hover:text-white">Cookies, analytics, and advertising</a></li>
          <li><a href="#sharing" className="underline hover:text-white">Sharing and legal disclosures</a></li>
          <li><a href="#international" className="underline hover:text-white">International use and cross-border processing</a></li>
          <li><a href="#retention" className="underline hover:text-white">Retention, deletion, and account closure</a></li>
          <li><a href="#rights" className="underline hover:text-white">Your rights and choices</a></li>
          <li><a href="#security" className="underline hover:text-white">Security safeguards</a></li>
          <li><a href="#children" className="underline hover:text-white">Children's privacy</a></li>
          <li><a href="#jurisdiction" className="underline hover:text-white">Jurisdiction-specific notes</a></li>
          <li><a href="#changes" className="underline hover:text-white">Changes to this policy</a></li>
          <li><a href="#contact" className="underline hover:text-white">How to contact us</a></li>
        </ol>
      </nav>

      {/* ─────────────────────────  1  ───────────────────────── */}
      {S("overview", 1, "Overview and about VeraLok")}
      <p>
        VeraLok is a Zero-Knowledge digital-trust platform. Its purpose is
        to let a person verify their identity to a regulated institution
        (for example, a bank, telecommunications company, or government
        agency) without that institution — or VeraLok itself — holding the
        person's raw government identifier.
      </p>
      <p>
        This Privacy Policy explains what personal information VeraLok
        collects, how VeraLok uses and protects it, who VeraLok shares it
        with, and the choices and rights you have. It applies to the
        VeraLok mobile app for iOS and Android, the VeraLok website
        (including <code>www.veralok.com</code>), and the VeraLok backend
        services that support them.
      </p>
      <p>
        VeraLok Holdings, Inc. ("<strong>VeraLok</strong>", "<strong>we</strong>",
        "<strong>us</strong>") is the entity responsible for personal
        information processed through the VeraLok platform. Institutions
        that use VeraLok to verify their own users remain independently
        responsible for their own use of the personal information VeraLok
        shares with them under your instruction.
      </p>
      <p>
        We use plain English throughout this policy. Where a technical
        concept has a real effect on your privacy — such as one-way
        cryptographic derivation, tokenisation, or device binding — we
        explain it in the same paragraph.
      </p>

      {/* ─────────────────────────  2  ───────────────────────── */}
      {S("collect", 2, "Information VeraLok collects")}
      <p>
        We collect only the information VeraLok's platform actually needs
        to run. We do not build behavioural or advertising profiles of
        our users. The categories below reflect what VeraLok's production
        systems collect today, organised as:
      </p>
      <ul>
        <li>account and contact information;</li>
        <li>identity-verification / document information;</li>
        <li>liveness / biometric information where applicable;</li>
        <li>device / technical information;</li>
        <li>consent, access and audit activity;</li>
        <li>communications and support information.</li>
      </ul>

      {SS("Account and contact information")}
      <p>
        When you create a VeraLok account we collect your full name, date
        of birth, country of residence, email address, and telephone
        number. Your password is stored only as a modern one-way hash;
        the password itself is never persisted or transmitted after
        registration.
      </p>

      {SS("Identity-verification and document information")}
      <p>
        Identity verification information. When you use VeraLok to verify
        your identity, we may process information contained in supported
        government-issued identity documents, such as a passport,
        driver's licence, or national ID, together with document images
        and relevant document details required to perform the
        verification. The full credential is used only long enough to
        run the verification. VeraLok does not keep the raw credential.
      </p>
      <p>
        The mobile app also captures on-device signals during enrolment —
        including a live selfie and structured document photographs —
        which are used to confirm you are the person shown on the
        document.
      </p>

      {SS("Liveness and biometric information")}
      <p>
        VeraLok uses on-device liveness signals and a facial similarity
        check during enrolment to confirm that the person completing
        enrolment is the same person shown on the document. On-device
        facial matching runs entirely inside the app; the underlying
        face-mesh embedding is not transmitted to VeraLok or to any
        third party.
      </p>

      {SS("Cryptographic and derived information")}
      <p>
        From the government credential you submit at enrolment, VeraLok
        derives and keeps <em>only</em> one-way cryptographic material:
      </p>
      <ul>
        <li>
          A <strong>disposal hash</strong> generated with a modern
          memory-hard, one-way hashing algorithm. This hash cannot be
          reversed to recover the raw credential.
        </li>
        <li>
          A <strong>dedupe fingerprint</strong> — a keyed one-way hash
          that lets VeraLok detect when the same credential is being
          enrolled a second time, without ever storing the credential
          itself.
        </li>
        <li>
          A <strong>person-composite fingerprint</strong> derived from
          your name, date of birth, and country. This lets VeraLok detect
          attempts to enrol the same real person under multiple different
          credentials.
        </li>
      </ul>
      <p>
        These derived values are what VeraLok uses to prevent duplicate
        or fraudulent identity issuance. They cannot be used to
        reconstruct your government identifier.
      </p>

      {SS("Wallet portrait photograph")}
      <p>
        If you choose to attach a personal photograph to your VeraLok
        Wallet credential, that photograph is uploaded to VeraLok's
        managed database and held there so it can be shown alongside your
        credential in the mobile app. Wallet-portrait submissions are
        reviewed by VeraLok before they are marked as verified for
        display; until they are reviewed, the mobile app labels them as
        citizen-submitted rather than VeraLok-verified. Attaching a
        photograph is optional; you can leave the credential without a
        photograph, and you can replace the photograph at any time.
      </p>

      {SS("Consent and authorisation records")}
      <p>
        When an institution asks to verify you, VeraLok creates a
        consent record with an internal consent identifier, the
        tokenised reference to your identity, the institution's
        VeraLok institutional identifier, the state of the
        decision (pending, granted, denied, or revoked), the purpose of
        the verification, the scope you consented to, and the exact time
        at which the decision was recorded. Each consent record carries
        a cryptographic fingerprint so we can detect after-the-fact
        tampering.
      </p>

      {SS("Verification and trust events")}
      <p>
        Each institutional verification produces a <em>trust event</em>{" "}
        row that records what action was taken, whether it succeeded,
        the tokenised reference to your identity, and the institution
        involved. These rows are stored as part of a cryptographically
        chained ledger so that historical rows cannot be modified without
        detection. Trust events use tokenised references, not your name
        or email, so they identify events without exposing your personal
        information outside VeraLok's own system.
      </p>

      {SS("Device and trusted-device information")}
      <p>
        When you use the VeraLok mobile app we record a limited set of
        device characteristics: the device model, the operating system
        version, a device identifier that your device generates locally
        for VeraLok, and a public cryptographic key that the device
        generates on your behalf so we can recognise it as a trusted
        device. The corresponding private key never leaves your device.
        If you enable push notifications, we also store the device's
        push-notification token issued by Apple or Google.
      </p>

      {SS("Technical, security, and operational information")}
      <p>
        To run VeraLok reliably and to detect abuse, we record request
        identifiers, timestamps, and outcome codes for the API calls
        your account makes. Where a log line records an action taken on
        your account, that line may include your VeraLok account
        identifier and your email address, so that VeraLok can
        distinguish who did what across its systems. Application logs do
        not include your government identifier or the raw contents of
        your documents.
      </p>

      {SS("Communications and support information")}
      <p>
        When you contact VeraLok through the website contact form,
        support channels or in-app support, we retain the message you
        send us together with the contact details you provide so that
        VeraLok can respond and keep a record of the request. Security
        submissions are routed separately from general support.
      </p>

      {/* ─────────────────────────  3  ───────────────────────── */}
      {S("not-collect", 3, "Information VeraLok does not collect")}
      <p>
        The VeraLok platform is deliberately narrow in what it collects.
        In particular:
      </p>
      <ul>
        <li>
          <strong>We do not retain your raw government identifier.</strong>{" "}
          The credential you present at enrolment is used to run
          verification and then discarded from VeraLok's own systems;
          only one-way cryptographic derivatives are kept.
        </li>
        <li>
          <strong>We do not store biometric templates.</strong> Face and
          liveness comparisons happen on your device. The resulting
          face-mesh embedding is not sent to VeraLok.
        </li>
        <li>
          <strong>We do not collect location.</strong> The VeraLok
          production platform does not read your GPS or approximate
          location. See our note under Jurisdiction-specific
          disclosures about a dormant iOS declaration.
        </li>
        <li>
          <strong>We do not access microphone or record audio.</strong>{" "}
          The VeraLok production app has no audio-capture functionality.
        </li>
        <li>
          <strong>We do not read the Android Advertising ID or the iOS
          IDFA.</strong>
        </li>
        <li>
          <strong>We do not embed third-party analytics or
          behavioural-tracking libraries</strong> in the VeraLok mobile
          app or on the VeraLok website.
        </li>
        <li>
          <strong>We do not track you on other websites</strong>, and we
          do not sell or rent your personal information to advertisers,
          data brokers, or marketing networks.
        </li>
      </ul>

      {/* ─────────────────────────  4  ───────────────────────── */}
      {S("use", 4, "How VeraLok uses this information")}
      <p>
        We use the information described above for a defined set of
        purposes:
      </p>
      <ul>
        <li>
          <strong>To provide the VeraLok service you signed up for</strong>{" "}
          — creating your account, verifying you, issuing your VeraLok identifier,
          maintaining your Wallet, and handling institutional
          verifications you authorise.
        </li>
        <li>
          <strong>To keep your account secure</strong> — sending sign-in
          alerts, driving multi-factor authentication where you have
          enabled it, recognising trusted devices, and detecting unusual
          activity.
        </li>
        <li>
          <strong>To prevent fraud and abuse</strong> — detecting
          duplicate enrolments, detecting attempts to enrol the same
          real person under different credentials, and protecting
          institutions who rely on VeraLok verifications.
        </li>
        <li>
          <strong>To meet legal and audit obligations</strong> — keeping
          the tamper-evident audit records that make VeraLok a trustable
          verification source.
        </li>
        <li>
          <strong>To communicate with you</strong> — sending welcome,
          verification, password-reset, and security-alert emails, and
          delivering the push notifications you subscribe to.
        </li>
        <li>
          <strong>To improve VeraLok</strong> — using aggregated,
          non-identifying signals about how the platform is used to
          decide what to build next. VeraLok does not use your personal
          information to train advertising models or third-party
          machine-learning services.
        </li>
      </ul>

      {SS("How we're allowed to process your data")}
      <p>
        Where local law asks us to name a legal basis for our processing,
        we rely on the following, whichever applies to you:
      </p>
      <ul>
        <li>
          <strong>Performance of a contract</strong> — to provide the
          VeraLok service you asked us to provide.
        </li>
        <li>
          <strong>Legitimate interest</strong> — to prevent fraud,
          protect the platform, and detect abuse.
        </li>
        <li>
          <strong>Consent</strong> — for any specific institutional
          verification you authorise. Consent is explicit and revocable
          at any time from within the VeraLok mobile app.
        </li>
        <li>
          <strong>Legal obligation</strong> — where the law we operate
          under requires retention of records.
        </li>
      </ul>
      <p>
        These bases apply globally, adapted where local law provides an
        equivalent concept.
      </p>

      {SS("Data minimisation")}
      <p>
        VeraLok is designed around the principle that the platform should
        hold as little identifying data as possible. The one-way
        cryptographic material described in §2 is the primary way we
        implement that principle: institutions can verify you against
        VeraLok, and VeraLok can prevent duplicate enrolment, without
        anyone holding your raw government identifier after the
        verification runs.
      </p>

      {/* ─────────────────────────  5  ───────────────────────── */}
      {S("verification", 5, "How identity verification works — from a privacy perspective")}
      <p>
        When you enrol, VeraLok temporarily receives the credential you
        submit so it can run the verification. During that verification:
      </p>
      <ul>
        <li>
          The credential may be transmitted to a specialist
          identity-verification service that VeraLok contracts with, for
          the duration of a single verification call. VeraLok does not
          store the copy the specialist provider handles; the
          specialist's own contract governs whatever temporary
          processing they perform.
        </li>
        <li>
          Live selfie and document photographs captured on-device are
          used, on-device, to compare the person taking the selfie with
          the person shown on the document. The on-device face-mesh
          embedding used for this comparison does not leave the device.
        </li>
        <li>
          Once the verification completes, VeraLok reduces the raw
          credential to the one-way cryptographic derivatives described
          in §2 and discards the raw credential from its own systems.
        </li>
      </ul>
      <p>
        Once you have a VeraLok identifier, subsequent institutional
        verifications do not require you to re-submit your raw
        credential. VeraLok validates you using its own tokenised
        references and the cryptographic material it already holds.
      </p>

      {SS("Institutional verification interactions")}
      <p>
        When an institution requests a verification from VeraLok, the
        request is recorded as a consent request. You see the request
        inside the VeraLok mobile app and can grant or deny it. If you
        grant it, VeraLok shares only the fields that are strictly
        necessary for the verification the institution is requesting;
        for many verifications this is only a signed confirmation of
        identity or age, not the underlying credential. If you deny or
        revoke the request, VeraLok records the decision and does not
        share the fields.
      </p>

      {/* ─────────────────────────  6  ───────────────────────── */}
      {S("providers", 6, "Service providers and processors")}
      <p>
        VeraLok uses a small number of reputable service providers to
        run the platform. Each provider is used only for the narrow
        purpose listed below, under a contract that requires them to
        keep your data confidential and to process it only on VeraLok's
        instructions.
      </p>

      {SS("Cloud infrastructure and database")}
      <p>
        VeraLok's backend runs on a major cloud-hosting provider and
        uses a managed database service. These providers host VeraLok's
        servers, storage, and audit records. VeraLok does not name a
        specific provider version or region in this policy because
        infrastructure choices evolve over time; but we use providers
        that offer contractual data-protection commitments appropriate
        for identity-related workloads.
      </p>

      {SS("Identity verification providers")}
      <p>
        For the initial verification of a government identity credential,
        VeraLok uses a specialist identity-verification provider as
        described in §5. Data shared with the provider is used only to
        run the verification. VeraLok does not use their systems to
        build a profile of you.
      </p>

      {SS("Store and payment processors")}
      <p>
        The VeraLok Citizen App is distributed as a one-time paid
        download through the Apple App Store and Google Play. Those
        stores handle the purchase directly with you and share only the
        transaction metadata VeraLok needs to confirm and account for
        the download. VeraLok does not operate a citizen subscription,
        does not offer in-app purchases, and does not receive or store
        your full card number.
      </p>

      {SS("Email delivery")}
      <p>
        VeraLok uses an established transactional-email provider to
        deliver the welcome, verification, password-reset, and
        security-alert emails our platform sends. Only the addressees
        and message metadata that are needed to deliver the email are
        shared with this provider. VeraLok does not use it for marketing.
      </p>

      {SS("Push notifications")}
      <p>
        On Android, VeraLok delivers push notifications through Firebase
        Cloud Messaging. On iOS, VeraLok delivers push notifications
        through the Apple Push Notification service. The services
        receive an opaque device token and the alert content that is to
        be delivered to your device.
      </p>

      {SS("On-device biometric matching")}
      <p>
        The VeraLok mobile app uses a well-known on-device face-mesh
        detector for liveness and document-versus-selfie matching. The
        detector runs entirely on your device; no image, embedding, or
        biometric derivative is transmitted to VeraLok or to the
        detector's vendor as part of that comparison.
      </p>

      {SS("Marketing-website assistant")}
      <p>
        The public VeraLok marketing website offers an "Ask VeraLok"
        assistant powered by a third-party large-language-model service.
        The messages you type into that assistant are transmitted to
        the language-model provider so it can generate a reply. Please
        do not share personal information with the assistant. Messages
        sent to the assistant are retained only in short-lived,
        in-memory session state and are not written to VeraLok's
        persistent databases.
      </p>

      {/* ─────────────────────────  7  ───────────────────────── */}
      {S("cookies", 7, "Cookies, website technologies, analytics, and advertising")}
      <p>
        The VeraLok website uses a small set of first-party cookies that
        are strictly necessary to keep you signed in and to protect your
        session from replay attacks. These cookies carry the tokens that
        represent your VeraLok session; they are marked HTTP-only and,
        on the production site, marked secure. See our
        {" "}<a href="/legal/cookies" className="underline">Cookie Policy</a>{" "}
        for details of each cookie's name, purpose, and lifetime.
      </p>
      <p>
        VeraLok does not use analytics cookies, advertising cookies, or
        cross-site tracking technologies. VeraLok does not embed
        third-party analytics libraries such as Google Analytics or any
        equivalent. VeraLok does not share information about you with
        advertising networks and does not participate in advertising
        auctions.
      </p>
      <p>
        Where you see the word "analytics" inside the VeraLok product
        (for example on the Institution Admin dashboard), it refers to
        VeraLok's own internal reporting on aggregated verification and
        consent activity — not to third-party analytics products.
      </p>

      {/* ─────────────────────────  8  ───────────────────────── */}
      {S("sharing", 8, "Sharing and legal disclosures")}
      <p>
        VeraLok does not sell your personal information. VeraLok shares
        your personal information only in the following situations:
      </p>
      <ul>
        <li>
          <strong>With institutions you authorise.</strong> When you
          grant an institutional verification, VeraLok shares only the
          fields the institution requested and you authorised. If you
          revoke the consent, VeraLok records the revocation and does
          not share the fields again.
        </li>
        <li>
          <strong>With our service providers.</strong> The narrow set of
          processors described in §6, only for the purposes described
          there.
        </li>
        <li>
          <strong>In a corporate transaction.</strong> If VeraLok is
          involved in a merger, acquisition, restructuring, financing,
          or sale of assets, personal information may be transferred to
          the counterparty subject to comparable protections. We will
          notify affected users in advance where the law requires it.
        </li>
        <li>
          <strong>To comply with law, protect rights, or respond to
          emergencies.</strong> VeraLok may disclose personal information
          when we have a good-faith belief that it is required by
          applicable law, legal process, or a valid government request;
          when it is necessary to protect VeraLok's rights, property, or
          safety, or that of others; or to prevent imminent harm.
        </li>
      </ul>
      <p>
        Where the law allows it, VeraLok reviews government and
        regulatory requests before responding, narrows their scope where
        possible, and challenges requests that appear overbroad. We
        strive to notify affected users of legal requests unless we are
        prohibited from doing so.
      </p>

      {/* ─────────────────────────  9  ───────────────────────── */}
      {S("international", 9, "International use and cross-border processing")}
      <p>
        VeraLok is operated from the United States. If you use VeraLok
        from another country, your personal information will be
        transferred to, stored in, and processed in the United States,
        and in any other country in which VeraLok or its service
        providers operate. Laws in those countries may differ from the
        laws in your home country.
      </p>
      <p>
        Where cross-border transfer safeguards are required by law
        (for example, the European Union's Standard Contractual Clauses
        or the United Kingdom's International Data Transfer Agreement),
        VeraLok relies on the applicable safeguard in its contracts with
        service providers.
      </p>

      {/* ─────────────────────────  10  ───────────────────────── */}
      {S("retention", 10, "Retention, deletion, and account closure")}
      <p>
        VeraLok keeps personal information only as long as it is needed
        for the purposes described in §4 and as required by applicable
        law and by our audit obligations. Different types of information
        have different retention profiles:
      </p>
      <ul>
        <li>
          <strong>Account and contact information</strong> — kept for as
          long as your VeraLok account is active.
        </li>
        <li>
          <strong>Cryptographic disposal material derived from your
          government credential</strong> — kept for the life of your
          VeraLok account so that VeraLok can detect duplicate
          enrolment of the same credential. Because this material is
          one-way, it cannot be reversed to your original credential.
        </li>
        <li>
          <strong>Consent records</strong> — kept for as long as required
          by law and by VeraLok's audit obligations, so that VeraLok can
          demonstrate what you did or did not authorise.
        </li>
        <li>
          <strong>Trust events</strong> — kept in tokenised form as part
          of the cryptographically chained audit ledger, so that the
          integrity of past verifications remains provable. Tokenised
          rows do not identify you outside VeraLok's own system.
        </li>
        <li>
          <strong>Wallet portrait photographs</strong> — kept for as
          long as they are attached to your Wallet. You may replace or
          remove a portrait at any time. Superseded submissions may be
          retained for a limited period as part of the review record.
        </li>
        <li>
          <strong>Application logs</strong> — kept for a limited
          operational period appropriate to running the platform and
          investigating security incidents.
        </li>
      </ul>

      {SS("Account deletion")}
      <p>
        You can ask VeraLok to close your account and delete your personal
        information by emailing{" "}
        <a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>{" "}
        from the address on the account. VeraLok will take reasonable
        steps to remove your personal information from its active
        systems. Where the law or VeraLok's audit obligations require
        us to retain a record, that record will be kept only in the
        minimal form the obligation requires, typically in the tokenised
        audit chain, so it does not identify you outside VeraLok's own
        system.
      </p>

      {SS("Audit and security-record retention")}
      <p>
        Because VeraLok's usefulness as a verification platform depends
        on the integrity of its historical records, some records —
        principally the tokenised audit chain and consent history —
        must be retained even after an account is closed. These records
        are kept in tokenised form so their integrity can be verified
        without exposing the underlying personal information.
      </p>

      {/* ─────────────────────────  11  ───────────────────────── */}
      {S("rights", 11, "Your rights and choices")}
      <p>
        Depending on where you live, you may have some or all of the
        following rights under local law. VeraLok respects the
        equivalent choices for every user, worldwide, so far as our
        platform can implement them:
      </p>
      <ul>
        <li>
          <strong>Access.</strong> Review the personal information
          VeraLok holds about you inside the VeraLok mobile app.
        </li>
        <li>
          <strong>Portability.</strong> Export a signed JSON copy of your
          verification and consent activity from the export centre in
          the mobile app; on paid plans a signed PDF summary is also
          available.
        </li>
        <li>
          <strong>Correction.</strong> Correct your contact information
          from within the app. To correct fields tied to your verified
          identity (such as your legal name) VeraLok may need to run a
          fresh verification.
        </li>
        <li>
          <strong>Deletion.</strong> Ask VeraLok to close your account
          and delete your personal information as described in §10.
        </li>
        <li>
          <strong>Consent revocation.</strong> Revoke any institutional
          verification consent immediately from within the app, without
          needing the institution's approval.
        </li>
        <li>
          <strong>Identity lock.</strong> Lock your VeraLok identity in
          one tap from the app. While locked, no institution can obtain
          a fresh verification against your identity until you unlock
          it.
        </li>
        <li>
          <strong>Objection.</strong> Object to a specific processing
          activity, and — where the local law allows it — request that
          we restrict processing while the objection is being
          considered.
        </li>
        <li>
          <strong>Non-discrimination.</strong> You will not be denied
          the VeraLok service, charged a different price, or given a
          different quality of service for exercising any of these
          rights.
        </li>
        <li>
          <strong>Complaint.</strong> Complain to your local
          data-protection, consumer-protection, or privacy authority.
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact{" "}
        <a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>{" "}
        from the email address on your VeraLok account.
      </p>

      {/* ─────────────────────────  12  ───────────────────────── */}
      {S("security", 12, "Security safeguards")}
      <p>
        VeraLok implements a defence-in-depth security programme
        appropriate to a platform that handles identity-related
        information:
      </p>
      <ul>
        <li>
          <strong>Encryption in transit.</strong> All traffic between
          your device and VeraLok is protected by current industry-
          standard transport encryption.
        </li>
        <li>
          <strong>Encryption at rest.</strong> Sensitive fields are
          protected with envelope encryption in VeraLok's managed
          database.
        </li>
        <li>
          <strong>One-way hashing.</strong> Passwords and cryptographic
          derivatives of government identifiers are protected with
          modern, memory-hard, one-way hashing.
        </li>
        <li>
          <strong>Tamper-evident audit chain.</strong> Audit records are
          cryptographically chained so that tampering with historical
          entries is detectable.
        </li>
        <li>
          <strong>Trusted-device binding.</strong> Sensitive citizen
          actions are cryptographically bound to a device you register
          with VeraLok, so a stolen password on its own is not enough
          to act as you.
        </li>
        <li>
          <strong>Role-based access control.</strong> Access to VeraLok's
          internal systems is scoped to the minimum roles necessary,
          logged, and monitored.
        </li>
        <li>
          <strong>Rate limiting and anomaly detection.</strong> VeraLok
          applies rate limits on sensitive endpoints and monitors for
          brute-force attempts, unusual sign-in patterns, and other
          abuse signals.
        </li>
      </ul>
      <p>
        No system can be made perfectly secure. If VeraLok becomes aware
        of a security incident that materially affects you, we will
        notify you in accordance with the law that applies where you
        live.
      </p>

      {/* ─────────────────────────  13  ───────────────────────── */}
      {S("children", 13, "Children's privacy")}
      <p>
        VeraLok is not directed at, and is not offered to, individuals
        under the applicable minimum age in their jurisdiction — for
        example, 13 in the United States and 16 in most of the European
        Union, unless a lower age is permitted by local law. VeraLok
        does not knowingly collect personal information from children
        below the applicable minimum age. If you believe a child has
        provided personal information to VeraLok, please contact
        {" "}<a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>{" "}
        and we will take appropriate action to remove it.
      </p>

      {/* ─────────────────────────  14  ───────────────────────── */}
      {S("jurisdiction", 14, "Jurisdiction-specific notes")}
      <p>
        This policy applies to everyone who uses VeraLok. The following
        notes describe how it interacts with a few specific frameworks;
        it is not an exhaustive list.
      </p>

      {SS("European Union / United Kingdom / European Economic Area")}
      <p>
        Where the General Data Protection Regulation, the UK GDPR, or
        equivalent European legislation applies, the legal bases we
        rely on are described in §4. You have the additional rights
        described in §11, including the right to lodge a complaint with
        the data-protection authority in the country where you live,
        where you work, or where you believe an infringement occurred.
      </p>

      {SS("California and other US states with comprehensive privacy laws")}
      <p>
        Where the California Consumer Privacy Act (as amended by the
        California Privacy Rights Act) or a comparable US state law
        applies, VeraLok's practice is: we do not sell your personal
        information, and we do not share your personal information for
        cross-context behavioural advertising. You may exercise your
        rights of access, deletion, correction, and non-discrimination
        by contacting{" "}
        <a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>.
        We will not require you to create an additional account to
        exercise these rights.
      </p>

      {SS("Nigeria, India, Brazil, and other jurisdictions with a data-protection framework")}
      <p>
        Where the Nigeria Data Protection Act, India's Digital Personal
        Data Protection Act, Brazil's LGPD, or a comparable local
        framework applies, VeraLok honours the equivalent access,
        correction, deletion, and consent-revocation rights those laws
        provide, subject to the retention obligations described in §10.
      </p>

      {SS("Note on dormant iOS permission strings")}
      <p>
        The current build of the VeraLok iOS app contains permission
        description strings for location and microphone that were
        carried over from an earlier codebase. These declarations are
        dormant: the VeraLok production platform does not read your
        location and does not record audio, as described in §3. VeraLok
        will remove the dormant declarations in a subsequent controlled
        mobile release. Until then, some app-store review interfaces
        may display those strings; they do not reflect actual VeraLok
        behaviour.
      </p>

      {/* ─────────────────────────  15  ───────────────────────── */}
      {S("changes", 15, "Changes to this policy")}
      <p>
        VeraLok may update this policy from time to time to reflect
        changes to our platform, our service providers, or the law.
        When we make a material change, we will update the effective
        date at the top of this page and, where the change is
        significant, notify you at least 30 days before the change
        takes effect by email, by an in-app notice, or both. Continued
        use of the VeraLok service after a change takes effect
        constitutes acceptance of the updated policy.
      </p>

      {/* ─────────────────────────  16  ───────────────────────── */}
      {S("contact", 16, "How to contact us")}
      <p>
        For any question, request, or concern relating to this Privacy
        Policy or to your personal information, please contact us:
      </p>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:privacy@veralok.io" className="underline">privacy@veralok.io</a>
        </li>
        <li>
          <strong>Postal:</strong> VeraLok Holdings, Inc., New Castle
          County, Delaware, United States.
        </li>
      </ul>
      <p>
        When you email us, please write from the email address on your
        VeraLok account, or include enough information for us to verify
        who you are before we act on your request.
      </p>
    </LegalPage>
  );
}
export function Terms() {
  return (
    <LegalPage title="Terms of Service" testid="legal-terms">
      <p>Effective 2026-07-17. Contact <code>legal@veralok.io</code>.</p>
      <p>Full text at <code>docs/legal/terms-of-service.md</code>.</p>
      <ul>
        <li>Acceptable use — no reverse-engineering, no shadow registry, no interference with the audit record.</li>
        <li>Data ownership — citizens own identity; institutions own tenant audit; the shared record is un-owned.</li>
        <li>Liability capped at 12 months' fees.</li>
        <li>Governing law — to be finalized by counsel.</li>
      </ul>
    </LegalPage>
  );
}
export function Cookies() {
  return (
    <LegalPage title="Cookie Policy" testid="legal-cookies">
      <p>
        VeraLok uses only functional, first-party cookies for authentication, session
        continuity, and user-interface preferences. No analytics. No advertising. No
        cross-site tracking. The full text is available at <code>docs/legal/cookie-policy.md</code>.
      </p>
    </LegalPage>
  );
}
export function SecurityLegal() {
  return (
    <LegalPage title="Security &amp; Compliance" testid="legal-security">
      <p>
        Zero-Knowledge identity handling · tamper-evident audit record · citizen-controlled
        consent · strict data isolation between institutions · TLS 1.3 in transit.
        Certifications in flight: SOC 2 Type II (2026 Q4), ISO 27001 (2027 Q2).
      </p>
      <p>
        Vulnerability disclosure: <a className="underline hover:text-white" href="mailto:security@veralok.io">security@veralok.io</a>.
        Full text at <code>docs/legal/security-and-compliance.md</code>.
      </p>
    </LegalPage>
  );
}


/**
 * VeraLok Support — public HTTPS page at /support.
 *
 * Purpose: satisfies the App Store Support URL requirement (Guideline 1.5
 * Safety — Developer Information) and gives real users a single place to
 * find help.
 *
 * Constraints:
 *   * Publicly accessible without authentication.
 *   * Mobile and desktop responsive — uses the existing MarketingLayout.
 *   * VeraLok branding — reuses LegalPage prose styles, no redesign.
 *   * No new features or unrelated cleanup.
 *   * No dependency on staging/dev routes.
 */
export function Support() {
  const S = (id, title) => (
    <h2 id={id} className="text-lg font-semibold text-white/90 mt-8 scroll-mt-24">
      {title}
    </h2>
  );
  return (
    <LegalPage title="VeraLok Support" testid="support-page">
      <p>
        Welcome to VeraLok Support. This page is the official support
        destination for the VeraLok mobile applications on iOS and
        Android and for the VeraLok website. Use the contact channels
        below to reach the VeraLok team, or scroll down for help with
        common tasks.
      </p>

      <div
        className="mt-6 mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-5"
        data-testid="support-contact-card"
      >
        <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-3">
          Contact VeraLok Support
        </div>
        <ul className="space-y-2">
          <li>
            <strong>General support:</strong>{" "}
            <a
              href="mailto:support@veralok.com"
              className="underline"
              data-testid="support-email"
            >
              support@veralok.com
            </a>
          </li>
          <li>
            <strong>Privacy requests:</strong>{" "}
            <a href="mailto:privacy@veralok.com" className="underline">
              privacy@veralok.com
            </a>
          </li>
          <li>
            <strong>Security disclosures:</strong>{" "}
            <a href="mailto:security@veralok.com" className="underline">
              security@veralok.com
            </a>
          </li>
          <li>
            <strong>Response time:</strong> most support enquiries receive
            a first response within two business days. Security
            disclosures are triaged the same business day.
          </li>
        </ul>
        <p className="mt-3 text-xs text-white/60">
          When you write to us, please send your message from the email
          address on your VeraLok account so we can verify who you are
          before we act on your request. Legacy <code>@veralok.io</code>{" "}
          addresses continue to be honored during the transition.
        </p>
      </div>

      {S("about", "About VeraLok")}
      <p>
        VeraLok is a Zero-Knowledge digital-trust platform. It lets a
        person verify their identity to a regulated institution — such
        as a bank, a telecommunications company, or a government agency —
        without that institution, or VeraLok itself, holding the raw
        government identifier. VeraLok is available as a mobile app for
        iOS and Android and as a set of web portals for institutional
        partners.
      </p>

      {S("common", "Common questions and answers")}

      <h3 className="text-base font-semibold text-white/85 mt-5">
        I can't sign in to my VeraLok account
      </h3>
      <p>
        Make sure you are using the email address you registered with,
        and that your device has an internet connection. If you forgot
        your password, tap "Forgot password" on the sign-in screen and
        follow the reset link that arrives by email — the link is valid
        for 30 minutes. If the email does not arrive, check your spam
        folder and confirm you are using the correct address. If you
        still cannot sign in, write to{" "}
        <a href="mailto:support@veralok.com" className="underline">
          support@veralok.com
        </a>{" "}
        from your registered email address and we will help you regain
        access.
      </p>

      <h3 className="text-base font-semibold text-white/85 mt-5">
        I did not receive the email verification link
      </h3>
      <p>
        VeraLok sends the verification email immediately after you
        create your account. If it has not arrived within a few
        minutes, check your spam folder. If it is not there, sign in to
        the VeraLok app and use the "Resend verification email"
        control, or contact support and we will trigger a fresh
        verification email for you.
      </p>

      <h3 className="text-base font-semibold text-white/85 mt-5">
        Identity enrolment is not accepting my details
      </h3>
      <p>
        VeraLok enrolment requires that the details you type match the
        government identity document you are about to capture. Enter
        your full legal name and date of birth exactly as they appear on
        the document. Select the country the document was issued in
        and the correct document type. If the platform reports that
        your credential combination is not supported in your country,
        please choose one of the eligible document types shown by the
        app, or contact support if you believe your combination should
        be accepted.
      </p>

      <h3 className="text-base font-semibold text-white/85 mt-5">
        I changed phones and want to keep my VeraLok identity
      </h3>
      <p>
        Your VeraLok identifier, Wallet, and consent history are tied to your
        VeraLok account, not to a single device. Install the VeraLok
        app on your new phone and sign in with your existing email and
        password — your Wallet and history will be there. For security,
        the trusted-device binding is created fresh on the new device;
        VeraLok will guide you through a short verification to
        authorise the new device before you can act on your identity
        from it. If your previous device was lost, stolen, or damaged,
        contact support and we will start a recovery.
      </p>

      <h3 className="text-base font-semibold text-white/85 mt-5">
        I would like to lock or delete my VeraLok identity
      </h3>
      <p>
        Any VeraLok user can lock their identity in one tap from the
        mobile app. While locked, no institution can obtain a fresh
        verification against your identity until you unlock it. To ask
        for your VeraLok account and personal information to be
        deleted, email{" "}
        <a href="mailto:privacy@veralok.com" className="underline">
          privacy@veralok.com
        </a>{" "}
        from the address on your account. We describe what happens to
        each category of information in the{" "}
        <a href="/legal/privacy" className="underline">
          VeraLok Privacy Policy
        </a>
        .
      </p>

      <h3 className="text-base font-semibold text-white/85 mt-5">
        I represent an institution that wants to integrate VeraLok
      </h3>
      <p>
        Institutional partners should reach the VeraLok team through the{" "}
        <a href="/contact" className="underline">
          Contact
        </a>{" "}
        page or by writing to{" "}
        <a href="mailto:partnerships@veralok.com" className="underline">
          partnerships@veralok.com
        </a>
        . Documentation for developers is available at{" "}
        <a href="/developers" className="underline">
          /developers
        </a>
        .
      </p>

      {S("platforms", "Supported platforms")}
      <p>
        The VeraLok Citizen app supports current versions of iOS and
        Android on hardware released within roughly the last five years.
        Enrolment requires a working camera. VeraLok is available in
        English at launch; additional languages will follow.
      </p>

      {S("privacy", "Privacy and security")}
      <p>
        VeraLok is designed around not holding your raw government
        identifier. What VeraLok collects, how it is used, and how you
        can access, correct, export, or delete your information is
        described in the{" "}
        <a href="/legal/privacy" className="underline">
          Privacy Policy
        </a>
        . For security-vulnerability disclosures, please email{" "}
        <a href="mailto:security@veralok.com" className="underline">
          security@veralok.com
        </a>
        .
      </p>

      {S("legal", "Legal and further information")}
      <ul>
        <li>
          <a href="/legal/privacy" className="underline">
            Privacy Policy
          </a>
        </li>
        <li>
          <a href="/legal/terms" className="underline">
            Terms of Service
          </a>
        </li>
        <li>
          <a href="/legal/cookies" className="underline">
            Cookie Policy
          </a>
        </li>
        <li>
          <a href="/legal/security" className="underline">
            Security &amp; Compliance
          </a>
        </li>
      </ul>

      {S("company", "Company")}
      <p>
        VeraLok Holdings Inc. · 251 Little Falls Drive, Wilmington,
        Delaware 19808, United States. For all support enquiries please use{" "}
        <a href="mailto:support@veralok.com" className="underline">
          support@veralok.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
